import styled, { css } from 'styled-components';
import { fadeIn } from '../../../__core/Themes/Animations';

export const CourseTopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 40px;
  background-color: ${({ theme }) => theme.UI_COLORS.primary};
  color: ${({ theme }) => theme.UI_COLORS.secondary};
  font-size: ${({ theme }) => theme.TYPOGRAPHY.bodyTextSmall.fontSize};
  font-weight: ${({ theme }) => theme.TYPOGRAPHY.bodyTextSmall.fontWeight};
  padding: 0 3rem;
  position: fixed;
  top: 60px;
  right: 0;
  width: 100%;
  box-sizing: border-box;
  z-index: 9;
  box-shadow: rgba(0, 0, 0, 0.06) 0 2px 3px, rgba(0, 0, 0, 0.05) 0 1px 0;

  .left-side-links {
    display: flex;
    align-items: center;
  }

  a {
    color: ${({ theme }) => theme.UI_COLORS.secondary};
    cursor: pointer;
    text-decoration: none;
    display: flex;
    align-items: center;

    &.backLink {
      svg {
        fill: ${({ theme }) => theme.UI_COLORS.secondary};
        margin-right: 0.5rem;
      }
    }

    &:hover {
      text-decoration: underline;
    }
  }
`;

export const CourseTopBarLinks = styled.div`
  display: none;
  align-items: center;

  & > a {
    margin-right: 3rem;
    & > span {
      padding-left: 0.5rem;
    }

    &:last-of-type {
      margin-right: 0;
    }
  }

  @media (${({ theme }) => theme.MEDIA_MIN.tablet}) {
    display: flex;
  }
`;

export const ModulesContainer = styled.div`
  margin: 0 auto;
  max-width: 820px;
  padding: 4rem 2rem;
`;

export const ModuleCardStyle = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.UI_COLORS.main_gray};
  padding-top: 4rem;
  padding-bottom: 4rem;
  animation: ${fadeIn} 0.5s;

  .info {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
  }

  .content {
    @media (${({ theme }) => theme.MEDIA_MIN.tablet}) {
      padding: 0 2rem;
    }
  }

  .moduleHeader {
    display: flex;
    align-items: center;
  }

  p {
    color: ${({ theme }) => theme.UI_COLORS.secondary};
    line-height: 2.6rem;
    margin: 2rem 0;
  }
`;

export const ModuleNumberStyle = styled.div`
  line-height: 4rem;
  font-size: 3.5rem;
  font-weight: 300;
  border: 1px solid ${({ theme }) => theme.UI_COLORS.secondary};
  border-radius: 6px;
  padding: 1rem;
  box-sizing: border-box;
  text-align: center;
  display: none;
  margin-right: 2rem;
  width: 60px;
  height: 60px;
  color: ${({ theme }) => theme.UI_COLORS.secondary};

  @media (${({ theme }) => theme.MEDIA_MIN.tablet}) {
    display: inline-block;
  }
`;

export const ModuleInfoStyle = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  & > h1 {
    font-weight: ${({ theme }) => theme.TYPOGRAPHY.h1.fontWeight};
    color: ${({ theme }) => theme.UI_COLORS.secondary};
    padding-bottom: 0.5rem;
    font-size: 2.4rem;
    margin: 0;

    @media (${({ theme }) => theme.MEDIA_MIN.tablet}) {
      font-size: ${({ theme }) => theme.TYPOGRAPHY.h1.fontSize};
    }
  }
`;

export const AdditionalInfoStyle = styled.div`
  font-size: 1.2rem;
  font-weight: 400;
  color: ${({ theme }) => theme.UI_COLORS.medium_gray};
  cursor: default;

  .withTitle {
    cursor: help;
  }
`;

export const ChaptersListStyle = styled.div`
  .actions {
    a {
      display: inline-block;
      margin-bottom: 1rem;
    }

    a {
      &:first-of-type {
        margin-right: 1rem;
      }
    }

    @media (${({ theme }) => theme.MEDIA_MIN.tablet}) {
      padding: 0 2rem;

      button {
        &:last-of-type {
          margin-left: 2rem;
        }
        &:nth-child(1) {
          margin-left: 0;
        }
      }
    }
  }

  .items {
    margin-top: 2rem;
  }
`;

export const ChapterTitle = styled.h2`
  font-weight: ${({ theme }) => theme.TYPOGRAPHY.h2.fontWeight};
  font-size: ${({ theme }) => theme.TYPOGRAPHY.h2.fontSize};
  color: ${({ theme }) => theme.UI_COLORS.secondary};
  margin: 0;
  text-decoration: none;
`;

export const ChapterItemStyle = styled.div<{
  access?: boolean;
  lastSeen?: boolean;
}>`
  padding: 3.5rem 3rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  border-radius: 6px;
  background-color: ${({ theme }) => theme.UI_COLORS.light};
  margin-bottom: 2rem;
  animation: ${fadeIn} 0.5s;
  display: block;
  text-decoration: none;
  transition: box-shadow 0.3s ease-out;
  animation: ${fadeIn} 0.5s;

  & > p {
    margin: 2rem 0;
  }

  .chapterHeader {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  ${({ access = true }) =>
    !access
      ? css`
          span,
          h2,
          p {
            filter: opacity(0.5);
          }
        `
      : css`
          &:hover {
            box-shadow: rgba(0, 0, 0, 0.35) 0 15px 20px;
          }
        `}

  ${({ lastSeen = false }) =>
    lastSeen &&
    css`
      border: 2px solid ${({ theme }) => theme.UI_COLORS.info};
    `}
`;

export const ChapterStatisticsStyle = styled.div`
  font-size: 1.2rem;
  font-weight: 400;
  color: ${({ theme }) => theme.UI_COLORS.medium_gray};
  & > span {
    &:after {
      content: '';
      margin: 0 0.5rem;
      border-left: 1px solid ${({ theme }) => theme.UI_COLORS.medium_gray};
    }

    &:last-of-type:after {
      content: none;
    }
  }
  @media (${({ theme }) => theme.MEDIA_MAX.mobile}) {
    display: flex;
    flex-wrap: wrap;

    & > span {
      display: block;
    }
  }
`;

export const CourseNameWithLogo = styled.div`
  display: flex;
  align-items: center;
  padding-top: 3rem;

  @media (${({ theme }) => theme.MEDIA_MIN.tablet}) {
    padding: 3rem 2rem 0 2rem;
  }

  & > h2 {
    font-size: ${({ theme }) => theme.TYPOGRAPHY.h2.fontSize};
    font-weight: 700;
    margin: 0;
  }

  & > div.course-icon {
    display: none;

    @media (${({ theme }) => theme.MEDIA_MIN.tablet}) {
      display: block;

      & > img {
        width: 60px;
        height: auto;
        margin-right: 2rem;
      }
    }
  }
`;
